/**
 * Enhance code blocks with copy functionality
 */
export class CodeEnhancer {
  private copyButtonClass = 'copy-button';
  private wrapperClass = 'code-block-wrapper';
  private copiedClass = 'copied';

  /**
   * Initialize code block enhancements
   */
  public init(): void {
    this.enhanceCodeBlocks();
  }

  /**
   * Enhance all code blocks on the page
   */
  private enhanceCodeBlocks(): void {
    const preElements = document.querySelectorAll('pre');

    preElements.forEach((pre) => {
      // Skip if already wrapped
      if (pre.parentElement?.classList.contains(this.wrapperClass)) {
        return;
      }

      this.wrapCodeBlock(pre);
    });
  }

  /**
   * Wrap a code block and add copy button
   */
  private wrapCodeBlock(pre: HTMLPreElement): void {
    // Create wrapper
    const wrapper = document.createElement('div');
    wrapper.className = this.wrapperClass;

    // Insert wrapper before pre and move pre into it
    pre.parentNode?.insertBefore(wrapper, pre);
    wrapper.appendChild(pre);

    // Create and add copy button
    const copyButton = this.createCopyButton(pre);
    wrapper.appendChild(copyButton);
  }

  /**
   * Create a copy button for a code block
   */
  private createCopyButton(pre: HTMLPreElement): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = this.copyButtonClass;
    button.textContent = 'Copy';
    button.setAttribute('aria-label', 'Copy code to clipboard');

    button.addEventListener('click', () => this.handleCopy(button, pre));

    return button;
  }

  /**
   * Handle copy button click
   */
  private async handleCopy(button: HTMLButtonElement, pre: HTMLPreElement): Promise<void> {
    const code = pre.querySelector('code') || pre;
    const text = code.textContent || '';

    try {
      await navigator.clipboard.writeText(text);
      this.showCopySuccess(button);
    } catch (err) {
      console.error('Failed to copy:', err);
      this.showCopyError(button);
    }
  }

  /**
   * Show copy success state
   */
  private showCopySuccess(button: HTMLButtonElement): void {
    button.textContent = 'Copied!';
    button.classList.add(this.copiedClass);

    setTimeout(() => {
      button.textContent = 'Copy';
      button.classList.remove(this.copiedClass);
    }, 2000);
  }

  /**
   * Show copy error state
   */
  private showCopyError(button: HTMLButtonElement): void {
    button.textContent = 'Failed';

    setTimeout(() => {
      button.textContent = 'Copy';
    }, 2000);
  }
}

